import type { EntityBase } from '../../types/Entities';
import type { StringKeys } from '../../types/utils';
import {
  append,
  assoc,
  concat,
  cond,
  equals,
  F,
  filter,
  findIndex,
  identity,
  ifElse,
  includes,
  isEmpty,
  isNil,
  lensIndex,
  lensProp,
  map,
  mergeDeepLeft,
  omit,
  over,
  pipe,
  propEq,
  propSatisfies,
  reduce,
  reject,
  T,
  without,
} from 'ramda';
import { isArrayWithItems } from '../array';
import { isWithoutConditions } from './conditions';

export const mergeOverrideWithEntity = <Override extends EntityBase, Entity extends EntityBase>(
  override: Override,
  entity: Entity,
): Entity => mergeDeepLeft(override, entity) as Entity;

export const applyOverrideToEntity = <Override extends EntityBase, Entity extends EntityBase>({
  override,
  entity,
  overrideToEntityReferenceFieldName,
  overrideToParentMultiReferenceFieldName,
}: {
  override: Override;
  entity: Entity;
  overrideToEntityReferenceFieldName: StringKeys<Override>;
  overrideToParentMultiReferenceFieldName?: StringKeys<Override>;
}): Entity => {
  const fieldOfOverrideToSkip = [
    'systemId',
    'conditions',
    'locale',
    overrideToEntityReferenceFieldName,
    String(overrideToParentMultiReferenceFieldName),
  ];

  const partialOverrideToApply = omit(fieldOfOverrideToSkip, override);

  // @ts-expect-error
  return mergeOverrideWithEntity(partialOverrideToApply, entity);
};

const updateEntityInListBySystemId = <T extends EntityBase>(
  list: T[],
  systemId: string,
  updater: (entity: T) => T,
): T[] => {
  const index = findIndex(propEq('systemId', systemId), list);

  if (index === -1) {
    return list;
  }

  return over(lensIndex(index), updater, list);
};

const linkChildToOriginalEntityMulti = <
  ChildEntity extends EntityBase,
  Entity extends EntityBase,
  Override extends EntityBase,
>({
  entity,
  override,
  childToEntityMultiReferenceFieldName,
  childToOverrideMultiReferenceFieldName,
}: {
  entity: Entity;
  override: Override;
  childToEntityMultiReferenceFieldName: StringKeys<ChildEntity>;
  childToOverrideMultiReferenceFieldName: StringKeys<ChildEntity>;
}) =>
  pipe<ChildEntity, ChildEntity, ChildEntity>(
    // modify [childToEntityMultiReferenceFieldName] field
    (child) => {
      const ids: string[] = child[childToEntityMultiReferenceFieldName] as any;
      return assoc(childToEntityMultiReferenceFieldName, append(entity.systemId, ids), child);
    },
    // modify [childToOverrideMultiReferenceFieldName] field
    (child) => {
      const ids: string[] = child[childToOverrideMultiReferenceFieldName] as any;
      return assoc(childToOverrideMultiReferenceFieldName, without([override.systemId], ids), child);
    },
  );

interface ApplyOverridesFactoryParams {
  isApplyingOverridesOnClient: boolean;
}

/**
 * For explanation on how different entities are linked have a look at docs/assets/overrides-links/overrides-links.png
 */
export const applyOverridesFactory =
  ({ isApplyingOverridesOnClient }: ApplyOverridesFactoryParams) =>
  <Entity extends EntityBase, Override extends EntityBase, ChildEntity extends EntityBase>({
    entityList,
    overridesList,
    childEntityList,
    overrideToEntityReferenceFieldName,
    childToEntityMultiReferenceFieldName,
    childToOverrideMultiReferenceFieldName,
    overrideToParentMultiReferenceFieldName,
    entityToParentMultiReferenceFieldName,
  }: {
    entityList: Entity[];
    overridesList: Override[];
    childEntityList: ChildEntity[];
    overrideToEntityReferenceFieldName: StringKeys<Override>;
    childToEntityMultiReferenceFieldName: StringKeys<ChildEntity>;
    childToOverrideMultiReferenceFieldName: StringKeys<ChildEntity>;
    entityToParentMultiReferenceFieldName?: StringKeys<Entity>;
    overrideToParentMultiReferenceFieldName?: StringKeys<Override>;
  }): [Entity[], ChildEntity[], Override[]] =>
    reduce(
      (resultEntitiesTuple, entity) => {
        const overridesToApply = pipe<Override[], Override[], Override[]>(
          filter(propEq(overrideToEntityReferenceFieldName, entity.systemId)),
          isApplyingOverridesOnClient
            ? // applying overrides on client - no need to filter further, we got everything we need
              identity
            : // override's conditions are already filtered.
              // It means that only server-applicable overrides will have empty conditions.
              // we need to apply only such overrides. Others will be applied on client
              filter(isWithoutConditions),
        )(overridesList);

        // don't return to client overrides that we are applying
        const overridesListForClient = without(overridesToApply, overridesList);

        return reduce(
          ([currentEntityList, currentChildEntityList], override) => {
            let newEntityList;
            let newChildEntityList = currentChildEntityList;

            const overrideParentRestrictedIds: string[] = overrideToParentMultiReferenceFieldName
              ? (override[overrideToParentMultiReferenceFieldName] as any)
              : [];

            const hasOverrideRestrictionsForSpecificParent = Boolean(
              entityToParentMultiReferenceFieldName && isArrayWithItems(overrideParentRestrictedIds),
            );
            if (hasOverrideRestrictionsForSpecificParent) {
              // override is restricted to specific parent - clone entity, link children to clone and apply override now

              // update entity: remove parent ids, that we are going to apply override for.
              // we will clone this entity with override applied, and link it to parent
              newEntityList = updateEntityInListBySystemId<Entity>(
                currentEntityList,
                entity.systemId,
                (_entity: Entity): Entity => {
                  if (!entityToParentMultiReferenceFieldName) {
                    return _entity;
                  }
                  const currentIds: string[] = _entity[entityToParentMultiReferenceFieldName] as any;
                  return assoc(
                    entityToParentMultiReferenceFieldName,
                    without(overrideParentRestrictedIds, currentIds),
                    _entity,
                  );
                },
              );

              const generateClonedEntityId = (parentId: string): string => `${entity.systemId}+${parentId}`;

              // cloning entities for restricted parents
              const newClonedEntities = map<string, Entity>(
                (parentId) =>
                  ({
                    ...applyOverrideToEntity({
                      override,
                      entity,
                      overrideToEntityReferenceFieldName,
                      overrideToParentMultiReferenceFieldName,
                    }),
                    systemId: generateClonedEntityId(parentId),
                    [entityToParentMultiReferenceFieldName!]: [parentId],
                  } as Entity),
                overrideParentRestrictedIds,
              );

              // adding cloned entities to entity list
              newEntityList = [...newEntityList, ...newClonedEntities];

              // update children: need to link children of original entity to cloned entities
              newChildEntityList = map((childEntity) => {
                const childToEntityIds: string[] = childEntity[childToEntityMultiReferenceFieldName] as any;
                if (childToEntityIds?.includes(entity.systemId)) {
                  return assoc(
                    childToEntityMultiReferenceFieldName,
                    concat(map(generateClonedEntityId, overrideParentRestrictedIds), childToEntityIds),
                    childEntity,
                  );
                }

                return childEntity;
              }, newChildEntityList);
            } else {
              // override is not restricted to specific parent - apply now
              newEntityList = updateEntityInListBySystemId<Entity>(
                currentEntityList,
                entity.systemId,
                (_entity: Entity): Entity =>
                  applyOverrideToEntity({
                    override,
                    entity: _entity,
                    overrideToEntityReferenceFieldName,
                    overrideToParentMultiReferenceFieldName,
                  }),
              );
            }

            // process multi reference fields
            const isChildOfOverride = propSatisfies(
              ifElse(isNil, F, includes(override.systemId)),
              childToOverrideMultiReferenceFieldName,
            );
            const childrenOfOverride = filter(isChildOfOverride, currentChildEntityList);

            if (!isEmpty(childrenOfOverride)) {
              const relinkMultiReferencedChild = cond([
                // unlink parentEntity's own items - multi reference field
                [
                  propSatisfies(ifElse(isNil, F, includes(entity.systemId)), childToEntityMultiReferenceFieldName),
                  over(lensProp(childToEntityMultiReferenceFieldName), reject(equals(entity.systemId))),
                ],
                // link child of override to original parentEntity
                [
                  isChildOfOverride,
                  linkChildToOriginalEntityMulti<ChildEntity, Entity, Override>({
                    entity,
                    override,
                    childToOverrideMultiReferenceFieldName,
                    childToEntityMultiReferenceFieldName,
                  }),
                ],
                // return child as is
                [T, identity],
              ]);

              newChildEntityList = map(relinkMultiReferencedChild, currentChildEntityList) as ChildEntity[];
            }

            return [newEntityList, newChildEntityList, overridesListForClient];
          },
          resultEntitiesTuple,
          overridesToApply,
        );
      },
      [entityList, childEntityList, overridesList],
      entityList,
    );
